import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ActiveUser } from '../../hooks/useCollaboration';
import { UserAvatar } from './ActiveUsersBar';
import { useTranslation } from 'react-i18next';

interface JoinNotificationProps {
  userJoinedEvent: ActiveUser | null;
  userLeftEvent: ActiveUser | null;
}

interface NotificationItem {
  id: string;
  user: ActiveUser;
  type: 'joined' | 'left';
}

const AUTO_DISMISS_MS = 4000;
// Cooldown entre deux notifications du même utilisateur (évite doublons StrictMode)
const DEBOUNCE_MS = 1500;

let notifCounter = 0;

const JoinNotification: React.FC<JoinNotificationProps> = ({
  userJoinedEvent,
  userLeftEvent,
}) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  // Garde la trace du dernier timestamp de notification par userId
  const lastNotifTimeRef = useRef<Record<string, number>>({});

  const addNotification = useCallback((user: ActiveUser, type: 'joined' | 'left') => {
    const now = Date.now();
    const lastTime = lastNotifTimeRef.current[user.user_id] || 0;

    // Dédoublonnage : ignore si le même utilisateur a déjà eu une notification récente
    if (now - lastTime < DEBOUNCE_MS) return;
    lastNotifTimeRef.current[user.user_id] = now;

    // Clé garantie unique via compteur atomique + timestamp
    notifCounter += 1;
    const id = `notif-${notifCounter}-${now}`;
    const item: NotificationItem = { id, user, type };

    setNotifications(prev => {
      // Max 3 notifications visibles simultanément
      const trimmed = prev.length >= 3 ? prev.slice(1) : prev;
      return [...trimmed, item];
    });

    // Auto-dismiss
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, AUTO_DISMISS_MS);
  }, []);

  useEffect(() => {
    if (userJoinedEvent) {
      addNotification(userJoinedEvent, 'joined');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userJoinedEvent]);

  useEffect(() => {
    if (userLeftEvent) {
      addNotification(userLeftEvent, 'left');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLeftEvent]);

  const dismiss = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  if (notifications.length === 0) return null;

  return (
    <div
      className="fixed z-50 flex flex-col gap-2"
      style={{ bottom: 80, left: 24, pointerEvents: 'none' }}
    >
      {notifications.map(notif => (
        <div
          key={notif.id}
          className="flex items-center gap-3 bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3 relative overflow-hidden"
          style={{
            pointerEvents: 'auto',
            animation: 'slideInLeft 0.3s ease-out',
            maxWidth: 280,
            borderLeft: `4px solid ${notif.user.color}`,
          }}
        >
          <UserAvatar user={notif.user} size={34} />
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-sm font-semibold text-gray-800 truncate">
              {notif.user.fullname}
            </span>
            <span className="text-xs text-gray-500">
              {notif.type === 'joined'
                ? t('features.swimlane.userJoinedMap')
                : t('features.swimlane.userLeftMap')}
            </span>
          </div>

          {/* Barre de progression auto-dismiss */}
          <div
            className="absolute bottom-0 left-0 h-0.5 rounded-full"
            style={{
              backgroundColor: notif.user.color,
              animation: `shrink ${AUTO_DISMISS_MS}ms linear forwards`,
              width: '100%',
            }}
          />

          <button
            onClick={() => dismiss(notif.id)}
            className="text-gray-400 hover:text-gray-600 leading-none flex-shrink-0 ml-1"
            style={{ fontSize: 18, lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
      ))}

      <style>{`
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes shrink {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default JoinNotification;
